import React, { useMemo } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Grid
} from '@mui/material';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const COLORS = ['#7b8cf0', '#4fc3b0', '#f2c26b', '#f28b98', '#a78bfa', '#7cc0f2'];
const GRID = 'rgba(148,163,184,0.22)';
const AXIS = { fill: '#94a3b8', fontSize: 12 };
const TOOLTIP_STYLE = {
    background: 'rgba(20,26,44,0.92)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    color: '#e6e9f5',
    fontSize: 12,
    backdropFilter: 'blur(8px)'
};

// Bar Chart Component
export function DashboardBarChart({ title, data, dataKey, valueKey }) {
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    {title}
                </Typography>
                <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                    <ResponsiveContainer>
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                            <XAxis dataKey={dataKey} tick={AXIS} axisLine={false} tickLine={false} />
                            <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                            <Legend />
                            <Bar dataKey={valueKey} fill="#7b8cf0" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// Line Chart Component
export function DashboardLineChart({ title, data, dataKey, valueKey }) {
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    {title}
                </Typography>
                <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                    <ResponsiveContainer>
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                            <XAxis dataKey={dataKey} tick={AXIS} axisLine={false} tickLine={false} />
                            <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                            <Legend />
                            <Line type="monotone" dataKey={valueKey} stroke="#7b8cf0" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// Pie Chart Component
export function DashboardPieChart({ title, data }) {
    return (
        <Card>
            <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    {title}
                </Typography>
                <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#7b8cf0"
                                dataKey="value"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// Overview Stats Chart
export function OverviewStatsChart({ jobs, applications, salarySpecs, invoices, contracts }) {
    const chartData = useMemo(() => [
        { name: 'Jobs', value: jobs },
        { name: 'Applications', value: applications },
        { name: 'Salary Specs', value: salarySpecs },
        { name: 'Invoices', value: invoices },
        { name: 'Contracts', value: contracts }
    ].filter(item => item.value > 0), [jobs, applications, salarySpecs, invoices, contracts]);

    return (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Overview Statistics
                </Typography>
                <Box sx={{ width: '100%', height: 260, mt: 1 }}>
                    <ResponsiveContainer>
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                            <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
                            <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                            <Bar dataKey="value" fill="#7b8cf0" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// Monthly Jobs Chart
export function MonthlyJobsChart({ jobs }) {
    const monthlyData = useMemo(() => {
        const months = {};
        jobs.forEach(job => {
            const date = new Date(job.created_at);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            months[monthKey] = (months[monthKey] || 0) + 1;
        });

        return Object.entries(months)
            .map(([month, count]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                count
            }))
            .sort((a, b) => new Date(a.month) - new Date(b.month))
            .slice(-6); // Last 6 months
    }, [jobs]);

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Jobs Created (Last 6 Months)
                </Typography>
                <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                    <ResponsiveContainer>
                        <LineChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                            <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                            <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                            <Legend />
                            <Line type="monotone" dataKey="count" stroke="#7b8cf0" strokeWidth={2} name="Jobs" />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// Invoice Status Chart
export function InvoiceStatusChart({ invoices }) {
    const statusData = useMemo(() => {
        const statusCounts = {
            paid: 0,
            pending: 0,
            cancelled: 0
        };

        invoices.forEach(invoice => {
            const status = invoice.status || 'pending';
            if (statusCounts.hasOwnProperty(status)) {
                statusCounts[status]++;
            }
        });

        return Object.entries(statusCounts)
            .filter(([_, count]) => count > 0)
            .map(([status, count]) => ({
                name: status.charAt(0).toUpperCase() + status.slice(1),
                value: count
            }));
    }, [invoices]);

    if (statusData.length === 0) {
        return null;
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Invoice Status Breakdown
                </Typography>
                <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={statusData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#7b8cf0"
                                dataKey="value"
                            >
                                {statusData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}

// Jobs vs Applications Chart
export function JobsVsApplicationsChart({ jobs = [], applications = [] }) {
    const chartData = useMemo(() => {
        const months = {};

        const addToMonth = (item, field) => {
            if (!item.created_at) return;
            const date = new Date(item.created_at);
            if (isNaN(date.getTime())) return;
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!months[key]) months[key] = { jobs: 0, applications: 0 };
            months[key][field]++;
        };

        jobs.forEach(job => addToMonth(job, 'jobs'));
        applications.forEach(app => addToMonth(app, 'applications'));

        return Object.entries(months)
            .map(([key, counts]) => ({
                month: new Date(key + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                sortKey: key,
                ...counts
            }))
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
            .slice(-6);
    }, [jobs, applications]);

    return (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
            <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Jobs vs Applications (Last 6 Months)
                </Typography>
                {chartData.length === 0 ? (
                    <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="text.disabled">
                            No activity data yet
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', height: 260, mt: 1 }}>
                        <ResponsiveContainer>
                            <BarChart data={chartData} barGap={4} barCategoryGap="28%">
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID} />
                                <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                                <YAxis allowDecimals={false} tick={AXIS} axisLine={false} tickLine={false} />
                                <Tooltip
                                    cursor={{ fill: 'rgba(148,163,184,0.12)' }} contentStyle={TOOLTIP_STYLE}
                                />
                                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                                <Bar dataKey="jobs" name="Jobs Posted" fill="#7b8cf0" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="applications" name="Applications" fill="#4fc3b0" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// Applications Over Time Chart
export function ApplicationsOverTimeChart({ applications }) {
    const monthlyData = useMemo(() => {
        const months = {};
        applications.forEach(app => {
            const date = new Date(app.created_at);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            months[monthKey] = (months[monthKey] || 0) + 1;
        });

        return Object.entries(months)
            .map(([month, count]) => ({
                month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
                count
            }))
            .sort((a, b) => new Date(a.month) - new Date(b.month))
            .slice(-6); // Last 6 months
    }, [applications]);

    if (monthlyData.length === 0) {
        return null;
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Applications Received (Last 6 Months)
                </Typography>
                <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                    <ResponsiveContainer>
                        <LineChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                            <XAxis dataKey="month" tick={AXIS} axisLine={false} tickLine={false} />
                            <YAxis tick={AXIS} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                            <Legend />
                            <Line type="monotone" dataKey="count" stroke="#4fc3b0" strokeWidth={2} name="Applications" />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
}
